// Pre-check-in settings as the editor shows them. Kept out of TripPreCheckSettings.js so the
// operator settings page can use them where its tests mock the editor away.

// Available field types for adding new fields
export const FIELD_TYPES = [
    { id: 'first_name', name: 'First name', type: 'string', required: true, mandatoryForLead: true },
    { id: 'last_name', name: 'Last name', type: 'string', required: false, mandatoryForLead: false },
    { id: 'email', name: 'Email', type: 'string', required: true, mandatoryForLead: true },
    { id: 'is_minor', name: 'Minor', type: 'boolean', required: false, mandatoryForLead: false },
    { id: 'phone', name: 'Phone number', type: 'string', required: false, mandatoryForLead: false },
    { id: 'custom_note', name: 'Custom note', type: 'string', required: false, mandatoryForLead: false },
    { id: 'date_of_birth', name: 'Date of birth', type: 'date', required: false, mandatoryForLead: false },
    { id: 'passport_number', name: 'Passport number', type: 'string', required: false, mandatoryForLead: false },
    { id: 'address', name: 'Address', type: 'string', required: false, mandatoryForLead: false },
    { id: 'emergency_contact', name: 'Emergency contact', type: 'string', required: false, mandatoryForLead: false },
];

// Helper function to safely convert to boolean
export const toBool = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    if (typeof value === 'number') return value === 1;
    return Boolean(value);
};

// The guest's check-in form always asks the custom note last (bookingmgmt ParticipantStep), so the
// editor shows it there and keeps it there (CP-6580).
export const customNoteLast = (list) => {
    const note = list.find((row) => row && row.id === 'custom_note');
    return note ? [...list.filter((row) => row !== note), note] : list;
};

// Helper function to get mandatory fields based on field type
export const getMandatoryFields = (fieldType) => {
    if (fieldType === 'lead_booker') {
        return FIELD_TYPES.filter(field => field.mandatoryForLead);
    } else {
        // For party members, no fields are mandatory anymore
        return [];
    }
};

// The settings the editor works on: defaults filled in, booleans converted from pseudo-boolean values,
// mandatory fields present. Every edit stores this filled-in copy, so compare two settings through it
// (CP-7540: an edit undone by hand never matched the server's JSON again, and the page stayed dirty).
// Filling in an already filled-in copy changes nothing.
export const normalizePreCheckSettings = (existingSettings = {}) => {
    // Default values
    const defaultSettings = {
        enabled: false,
        required: false,
        intro_text: '',
        confirmation_message: '',
        lead_booker_fields: FIELD_TYPES.filter(field => field.mandatoryForLead),
        party_member_fields: [],
        allow_edit_after_checkin: false,
        collect_party_members_info: false,
        include_party_members_in_chat: false,
        waiver_enabled: false,
        waivers: [],
        apply_same_fields_to_party_members: false,
    };

    // Merge with existing settings, but preserve existing values when they exist
    const baseSettings = {
        ...defaultSettings,
        ...existingSettings,
        // Ensure boolean fields are properly converted from pseudo-boolean values
        enabled: toBool(existingSettings.enabled ?? defaultSettings.enabled),
        required: toBool(existingSettings.required ?? defaultSettings.required),
        allow_edit_after_checkin: toBool(existingSettings.allow_edit_after_checkin ?? defaultSettings.allow_edit_after_checkin),
        waiver_enabled: toBool(existingSettings.waiver_enabled ?? defaultSettings.waiver_enabled),
        apply_same_fields_to_party_members: toBool(existingSettings.apply_same_fields_to_party_members ?? defaultSettings.apply_same_fields_to_party_members),
        collect_party_members_info: toBool(existingSettings.collect_party_members_info ?? defaultSettings.collect_party_members_info),
        include_party_members_in_chat: toBool(existingSettings.include_party_members_in_chat ?? defaultSettings.include_party_members_in_chat),
    };

    // Ensure mandatory fields are always present and enabled
    const ensureMandatoryFields = (fields = [], fieldType) => {
        const mandatoryFields = getMandatoryFields(fieldType);
        const existingFieldIds = fields.map(f => f.id.replace(/_\d+$/, ''));

        // Add any missing mandatory fields
        const missingMandatoryFields = mandatoryFields.filter(mandatory =>
            !existingFieldIds.includes(mandatory.id)
        );

        const allFields = [...fields, ...missingMandatoryFields];

        // For party members, ensure is_minor is always first if it exists (for UX)
        if (fieldType === 'party_member' && allFields.length > 0) {
            const minorField = allFields.find(f => f.id === 'is_minor');
            const otherFields = allFields.filter(f => f.id !== 'is_minor');

            return customNoteLast(minorField ? [minorField, ...otherFields] : allFields);
        }

        return customNoteLast(allFields);
    };

    return {
        ...baseSettings,
        lead_booker_fields: ensureMandatoryFields(baseSettings.lead_booker_fields, 'lead_booker'),
        party_member_fields: ensureMandatoryFields(baseSettings.party_member_fields, 'party_member'),
    };
};
